import { TableTypeSlug } from 'models/companies'
import { UnifiedTargetType } from 'services/opportunities/targetTypeMap'

export interface ResolveDealTableSlugParams {
  /** The table the create-deals request originates from. */
  tableTypeSlug: TableTypeSlug
  /** Present when the source table reads from the unified /opportunities/ engine. */
  unifiedTargetType?: UnifiedTargetType
  /** True when the user selected "all rows" (across pages), not explicit ids. */
  isAllRowsSelected?: boolean
  /** True when the selection is doors rather than chains/companies. */
  forDoorSelection?: boolean
  /** Set when targeting a single door by its proxy id. */
  targetDoorProxyId?: number
}

const REPORTS_TABLE_SLUGS: ReadonlySet<TableTypeSlug> = new Set([
  'chains-reports-table',
  'doors-reports-table',
])

/**
 * Resolve which table slug a bulk "create deals" request routes through.
 *
 * The reverted restaurant reports tables (`chains-reports-table` /
 * `doors-reports-table`) no longer send `unifiedTargetType`, so a *select-all*
 * on them must keep its reports slug to route to `reports/{chains,doors}/bulk/
 * deals/` — the same raw-SQL engine as the list, which re-resolves the filtered
 * set server-side (incl. the elastic menu/name search). Without this, select-all
 * falls through to the contacts chokepoint, whose narrower filterset silently
 * drops the reports-only filters + ES and creates deals for the wrong (larger)
 * set. Explicit id selection (incl. detail/mobile) still routes by id: a door →
 * `unmatched-doors-table`, otherwise `companies-table`.
 *
 * @param params - the create-deals source context (see field docs above)
 * @returns the table slug to pass to `commonCreateDeals`
 */
export function resolveDealTableSlug(
  params: ResolveDealTableSlugParams
): TableTypeSlug {
  const isReportsTableSlug = REPORTS_TABLE_SLUGS.has(params.tableTypeSlug)

  if (
    params.unifiedTargetType ||
    (params.isAllRowsSelected && isReportsTableSlug)
  ) {
    return params.tableTypeSlug
  }

  if (params.forDoorSelection || params.targetDoorProxyId) {
    return 'unmatched-doors-table'
  }

  return 'companies-table'
}
